/**
 * AuthService — appels d'authentification vers le backend FastAPI UNIQUE.
 *
 * Endpoints consommés :
 *   POST /auth/register
 *   POST /auth/login
 *   GET  /auth/me
 *   GET  /subscription/status
 */
import type { PlanKey } from '@/lib/plans';

const BACKEND_URL ='https://unique.yaunid.com/api';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  plan: PlanKey;
  plan_label: string;
}

export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

export class AuthError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
  } catch {
    throw new AuthError(`Serveur inaccessible (${BACKEND_URL})`);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AuthError(data.detail ?? `Erreur ${res.status}`, res.status);
  }
  return data as T;
}

export const AuthService = {
  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ): Promise<AuthTokenResponse> {
    return request<AuthTokenResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      }),
    });
  },

  async login(email: string, password: string): Promise<AuthTokenResponse> {
    return request<AuthTokenResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async me(token: string): Promise<AuthUser> {
    const res = await request<{ success: boolean; data: AuthUser }>('/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getSubscriptionStatus(token: string) {
    const res = await request<{ success: boolean; data: unknown }>(
      '/subscription/status',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data as {
      plan: PlanKey;
      plan_label: string;
      limits: Record<string, unknown>;
      subscription: unknown | null;
    };
  },
};
